'use client';

import NextLink from '@/components/next-link';
import usePromotionImpression from '@/hooks/usePromotionImpression';
import { fireSelectPromotion } from '@/utils/gtm/helper';
import { useLocale } from 'next-intl';
import { useRef } from 'react';

// A swipe on a Swiper slide still ends in a click on the anchor. Anything that
// travelled further than this between pointerdown and click is a drag, and the
// spec says a drag is not a promotion selection.
const DRAG_THRESHOLD_PX = 10;

/**
 * A link around a promotional creative. Reports `view_promotion` once the
 * creative has been half visible for the dwell time, and `select_promotion`
 * with the same identifiers when it is genuinely clicked.
 *
 * `href` is the resolved route, which becomes the absolute
 * `promotion_destination`.
 */
const PromotionLink = ({
  promotion,
  href,
  onClick,
  oncePerSession = false,
  children,
  ...props
}) => {
  const promotionRef = usePromotionImpression(promotion, { oncePerSession });
  const locale = useLocale();
  const pointerOrigin = useRef(null);

  const handlePointerDown = (event) => {
    pointerOrigin.current = { x: event.clientX, y: event.clientY };
  };

  const handleClick = (event) => {
    const origin = pointerOrigin.current;
    pointerOrigin.current = null;

    // No recorded origin means the link was activated by keyboard, which is a
    // real selection.
    const dragged =
      origin &&
      Math.hypot(event.clientX - origin.x, event.clientY - origin.y) >
        DRAG_THRESHOLD_PX;

    if (!dragged) fireSelectPromotion(promotion, href, locale);

    onClick?.(event);
  };

  return (
    <NextLink
      ref={promotionRef}
      href={href}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      {...props}
    >
      {children}
    </NextLink>
  );
};

export default PromotionLink;
